import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import Skeleton from 'components/skeleton/Skeleton';
import { SportsIcon, ChevronDownIcon } from 'components/icons';
import { formatLocalTime } from 'utils/formatDate';
import styles from './sportsHistory.module.scss';

export interface SportsbookSelection {
  sport?: string;
  tournament?: string;
  event?: string;
  market?: string;
  pick?: string | number;
  odds?: string | number;
  live?: boolean;
  startAt?: string;
  status?: string;
  score?: string | null;
}

export interface SportsbookBetItem {
  ticketId?: string | number;
  placedAt?: string;
  settledAt?: string | null;
  currency?: string;
  stake?: string | number;
  payout?: string | number;
  odds?: string | number;
  status?: string;
  selections?: SportsbookSelection[];

  // Fallbacks
  id?: string | number;
  betId?: string | number;
  eventTitle?: string;
  eventName?: string;
  match?: string;
  gameTitle?: string;
  title?: string;
  sport?: string;
  sportName?: string;
  selection?: string;
  selectionName?: string;
  market?: string;
  betType?: string;
  type?: string;
  betAmount?: number | string;
  amount?: number | string;
  multiplier?: number | string;
  coefficient?: number | string;
  totalOdds?: number | string;
  payoutAmount?: number | string;
  winAmount?: number | string;
  date?: string;
  createdAt?: string;
  timestamp?: string | number;
  [key: string]: unknown;
}

interface SportsHistoryTableProps {
  data: SportsbookBetItem[];
  loading: boolean;
  limit: number;
}

const formatSportName = (sport?: string): string => {
  if (!sport) return '';
  const s = sport.trim().toLowerCase();
  if (s === 'dota2') return 'Dota 2';
  if (s === 'csgo' || s === 'cs2' || s === 'counterstrike') return 'CS2';
  if (s === 'lol' || s === 'leagueoflegends') return 'LoL';
  if (s === 'valorant') return 'Valorant';
  if (s === 'basketball') return 'Basketball';
  if (s === 'football' || s === 'soccer') return 'Football';
  if (s === 'tennis') return 'Tennis';
  if (s === 'mma' || s === 'ufc') return 'MMA';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const formatMarketName = (market?: string): string => {
  if (!market) return '';
  const m = market.trim();
  if (m.toLowerCase() === 'winner') return 'Winner';
  if (m.toLowerCase() === '1x2') return '1X2';
  return m.charAt(0).toUpperCase() + m.slice(1);
};

const formatPickName = (pick?: string | number, event?: string): string => {
  if (pick === undefined || pick === null) return '';
  const p = String(pick).trim();
  if (!event) return p;

  if (p === '1' || p === '2') {
    const teams = event.split(/\s+(?:[–—-]|vs\.?|v)\s+/i);
    if (teams.length >= 2) {
      const teamRaw = p === '1' ? teams[0] : teams[1];
      const cleanTeam = teamRaw.replace(/\s*\([^)]*\)\s*$/, '').trim();
      return cleanTeam ? `${cleanTeam} (${p})` : p;
    }
  }

  return p;
};

const SportsHistoryTable: React.FC<SportsHistoryTableProps> = ({ data, loading, limit }) => {
  const { t } = useTranslation();
  const [expandedTicketId, setExpandedTicketId] = useState<string | number | null>(null);

  const toggleExpand = (id: string | number) => {
    setExpandedTicketId((prev) => (prev === id ? null : id));
  };

  return (
    <div className={styles.responsiveScroll}>
      <div className={styles.tableMinWidth}>
        <div className={styles.thead}>
          <div className={styles.tr}>
            <span>{t('sportsHistory.columns.event', t('casinoHistory.columns.game', 'Event'))}</span>
            <span>{t('sportsHistory.columns.betAmount', t('casinoHistory.columns.betAmount', 'Bet amount'))}</span>
            <span>{t('sportsHistory.columns.multiplier', t('casinoHistory.columns.multiplier', 'Odds'))}</span>
            <span>{t('sportsHistory.columns.payout', t('casinoHistory.columns.payout', 'Payout'))}</span>
            <span>{t('sportsHistory.columns.date', t('casinoHistory.columns.date', 'Date'))}</span>
          </div>
        </div>

        <div className={styles.tbody}>
          {loading ? (
            Array.from({ length: limit }).map((_, idx) => (
              <div key={idx} className={styles.trBody}>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>
                    {t('sportsHistory.columns.event', t('casinoHistory.columns.game', 'Event'))}
                  </span>
                  <Skeleton style={{ width: '28px', height: '28px', borderRadius: '6px' }} />
                  <Skeleton
                    style={{
                      width: '100px',
                      height: '14px',
                      borderRadius: '4px',
                      marginLeft: '8px',
                    }}
                  />
                </div>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>
                    {t('sportsHistory.columns.betAmount', t('casinoHistory.columns.betAmount', 'Bet amount'))}
                  </span>
                  <Skeleton style={{ width: '50px', height: '14px', borderRadius: '4px' }} />
                </div>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>
                    {t('sportsHistory.columns.multiplier', t('casinoHistory.columns.multiplier', 'Odds'))}
                  </span>
                  <Skeleton style={{ width: '40px', height: '14px', borderRadius: '4px' }} />
                </div>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>
                    {t('sportsHistory.columns.payout', t('casinoHistory.columns.payout', 'Payout'))}
                  </span>
                  <Skeleton style={{ width: '60px', height: '14px', borderRadius: '4px' }} />
                </div>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>
                    {t('sportsHistory.columns.date', t('casinoHistory.columns.date', 'Date'))}
                  </span>
                  <Skeleton style={{ width: '60px', height: '14px', borderRadius: '4px' }} />
                </div>
              </div>
            ))
          ) : data.length === 0 ? (
            <div className={styles.emptyState}>
              {t('sportsHistory.emptyState', 'No sportsbook history found')}
            </div>
          ) : (
            data.map((item: SportsbookBetItem, idx: number) => {
              const ticketId = item.ticketId ?? item.id ?? item.betId ?? idx;
              const isExpanded = expandedTicketId === ticketId;

              const selections = item.selections && item.selections.length > 0 ? item.selections : [];
              const primarySelection = selections[0];

              const eventTitle =
                primarySelection?.event ||
                item.eventTitle ||
                item.eventName ||
                item.match ||
                item.gameTitle ||
                item.title ||
                item.sport ||
                item.sportName ||
                (item.ticketId ? `Ticket #${item.ticketId}` : t('sportsHistory.unknownEvent', 'Sports Bet'));

              const formattedSport = formatSportName(primarySelection?.sport || (item.sport as string | undefined));
              const tournament = primarySelection?.tournament;
              const formattedMarket = formatMarketName(primarySelection?.market);
              const formattedPick = formatPickName(primarySelection?.pick, primarySelection?.event);

              const subtitleParts: string[] = [];
              if (formattedSport) {
                subtitleParts.push(formattedSport);
              }
              if (tournament) {
                subtitleParts.push(tournament);
              }
              if (formattedPick) {
                const pickLabel = formattedMarket ? `${formattedMarket}: ${formattedPick}` : `Pick: ${formattedPick}`;
                subtitleParts.push(pickLabel);
              }
              if (selections.length > 1) {
                subtitleParts.push(`+${selections.length - 1} more`);
              } else if (item.ticketId) {
                subtitleParts.push(`#${item.ticketId}`);
              }
              const eventSubtitle = subtitleParts.join(' • ');

              const currencyCode = item.currency || 'USD';
              const betVal = parseFloat(String(item.stake ?? item.betAmount ?? item.amount ?? '0'));
              const payoutVal = parseFloat(String(item.payout ?? item.payoutAmount ?? item.winAmount ?? '0'));
              const rawOdds =
                item.odds ??
                item.multiplier ??
                item.coefficient ??
                item.totalOdds ??
                primarySelection?.odds ??
                '-';
              const oddsNum = parseFloat(String(rawOdds));
              const formattedOdds =
                typeof rawOdds === 'number'
                  ? rawOdds.toFixed(2)
                  : !isNaN(oddsNum)
                    ? oddsNum.toFixed(2)
                    : rawOdds;

              const potentialPayout = betVal > 0 && !isNaN(oddsNum) && oddsNum > 0 ? betVal * oddsNum : 0;

              const statusLower = (item.status || primarySelection?.status || '').toLowerCase();
              const isOpen = statusLower === 'open';
              const isPending =
                isOpen ||
                statusLower === 'pending' ||
                statusLower === 'in_progress' ||
                statusLower === 'active';
              const isCancelled =
                statusLower === 'cancelled' ||
                statusLower === 'refunded' ||
                statusLower === 'void';
              const isWin =
                !isPending &&
                !isCancelled &&
                (statusLower === 'won' || statusLower === 'win' || payoutVal > 0);

              const dateVal = item.placedAt || item.settledAt || item.createdAt || item.date || item.timestamp;

              return (
                <div key={ticketId} className={styles.ticketRowWrapper}>
                  <div
                    className={cx(styles.trBody, { [styles.activeRow]: isExpanded })}
                    onClick={() => toggleExpand(ticketId)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleExpand(ticketId);
                      }
                    }}
                  >
                    <div className={styles.td}>
                      <span className={styles.mobileLabel}>
                        {t('sportsHistory.columns.event', t('casinoHistory.columns.game', 'Event'))}
                      </span>
                      <div className={styles.sportInfo}>
                        <div className={styles.sportBadge}>
                          <SportsIcon />
                        </div>
                        <div className={styles.sportTextWrapper}>
                          <span className={styles.eventName} title={eventTitle}>
                            {eventTitle}
                          </span>
                          {eventSubtitle ? (
                            <span className={styles.eventSubtitle} title={eventSubtitle}>
                              {eventSubtitle}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className={styles.td}>
                      <span className={styles.mobileLabel}>
                        {t('sportsHistory.columns.betAmount', t('casinoHistory.columns.betAmount', 'Bet amount'))}
                      </span>
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode }).format(betVal)}
                    </div>

                    <div className={styles.td}>
                      <span className={styles.mobileLabel}>
                        {t('sportsHistory.columns.multiplier', t('casinoHistory.columns.multiplier', 'Odds'))}
                      </span>
                      {formattedOdds}
                    </div>

                    <div
                      className={cx(styles.td, {
                        [styles.win]: isWin,
                        [styles.loss]: !isWin && !isPending && !isCancelled,
                        [styles.pending]: isPending,
                        [styles.cancelled]: isCancelled,
                      })}
                    >
                      <span className={styles.mobileLabel}>
                        {t('sportsHistory.columns.payout', t('casinoHistory.columns.payout', 'Payout'))}
                      </span>
                      {isOpen ? (
                        <div className={styles.payoutStack}>
                          <span className={styles.statusBadgeOpen}>{t('sportsHistory.status.open', 'Open')}</span>
                          {potentialPayout > 0 && (
                            <span
                              className={styles.potentialWin}
                              title={t('sportsHistory.potentialPayout', 'Est. Payout')}
                            >
                              ({new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode }).format(potentialPayout)})
                            </span>
                          )}
                        </div>
                      ) : isPending ? (
                        <div className={styles.payoutStack}>
                          <span className={styles.statusBadgeOpen}>{t('sportsHistory.status.pending', 'Pending')}</span>
                          {potentialPayout > 0 && (
                            <span
                              className={styles.potentialWin}
                              title={t('sportsHistory.potentialPayout', 'Est. Payout')}
                            >
                              ({new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode }).format(potentialPayout)})
                            </span>
                          )}
                        </div>
                      ) : isCancelled ? (
                        t('sportsHistory.status.cancelled', 'Refunded')
                      ) : isWin ? (
                        new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode }).format(payoutVal)
                      ) : (
                        new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode }).format(0)
                      )}
                    </div>

                    <div className={styles.td}>
                      <span className={styles.mobileLabel}>
                        {t('sportsHistory.columns.date', t('casinoHistory.columns.date', 'Date'))}
                      </span>
                      <div className={styles.dateAndToggle}>
                        <span>{dateVal ? formatLocalTime(dateVal) : '-'}</span>
                        <span className={cx(styles.expandChevron, { [styles.expandedChevron]: isExpanded })}>
                          <ChevronDownIcon />
                        </span>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className={styles.ticketDetailsDrawer}>
                      <div className={styles.ticketMetaHeader}>
                        <div className={styles.metaBadgeGroup}>
                          <span className={styles.metaBadge}>
                            {t('sportsHistory.ticketId', 'Ticket')} #{ticketId}
                          </span>
                          <span className={styles.metaBadgeType}>
                            {selections.length > 1
                              ? `${t('sportsHistory.expressBet', 'Express')} (${selections.length})`
                              : t('sportsHistory.singleBet', 'Single')}
                          </span>
                          {primarySelection?.live && (
                            <span className={styles.metaBadgeLive}>{t('sportsHistory.live', 'LIVE')}</span>
                          )}
                        </div>
                        {potentialPayout > 0 && isOpen && (
                          <div className={styles.metaPotential}>
                            <span className={styles.metaPotentialLabel}>
                              {t('sportsHistory.potentialPayout', 'Est. Payout')}:
                            </span>
                            <span className={styles.metaPotentialValue}>
                              {new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode }).format(
                                potentialPayout,
                              )}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className={styles.selectionsList}>
                        {(selections.length > 0 ? selections : [primarySelection]).filter(Boolean).map((sel, sIdx) => {
                          const selSport = formatSportName(sel.sport);
                          const selTournament = sel.tournament;
                          const selMarket = formatMarketName(sel.market);
                          const selPick = formatPickName(sel.pick, sel.event);
                          const selRawOdds = sel.odds ?? '-';
                          const selFormattedOdds =
                            typeof selRawOdds === 'number'
                              ? selRawOdds.toFixed(2)
                              : !isNaN(parseFloat(String(selRawOdds)))
                                ? parseFloat(String(selRawOdds)).toFixed(2)
                                : selRawOdds;

                          return (
                            <div key={sIdx} className={styles.selectionItem}>
                              <div className={styles.selectionMain}>
                                <div className={styles.selectionHeader}>
                                  <span className={styles.selectionSportTournament}>
                                    {selSport}
                                    {selTournament ? ` • ${selTournament}` : ''}
                                  </span>
                                  {sel.live && (
                                    <span className={styles.liveTag}>{t('sportsHistory.live', 'LIVE')}</span>
                                  )}
                                  {sel.startAt && (
                                    <span className={styles.selectionStartAt}>
                                      {t('sportsHistory.startTime', 'Starts')}: {formatLocalTime(sel.startAt)}
                                    </span>
                                  )}
                                </div>
                                <div className={styles.selectionEventTitle}>{sel.event || eventTitle}</div>
                                {sel.score && (
                                  <div className={styles.selectionScore}>
                                    Score: <strong>{sel.score}</strong>
                                  </div>
                                )}
                              </div>

                              <div className={styles.selectionOutcome}>
                                <div className={styles.outcomePick}>
                                  <span className={styles.outcomeMarket}>{selMarket || 'Pick'}</span>
                                  <span className={styles.outcomeVal}>{selPick}</span>
                                </div>
                                <div className={styles.outcomeOdds}>@{selFormattedOdds}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default SportsHistoryTable;
